/**
 * AudioManager - Core audio system using Web Audio API
 * Handles sound playback, crossfading, volume control, and resource management
 */

import { SOUND_REGISTRY } from './soundRegistry.js';
import { AUDIO_CONFIG } from '../config/audioConfig.js';

export class AudioManager {
  constructor() {
    // Initialize Web Audio API context
    this.audioContext = null;
    this.initialized = false;

    // Audio buffer cache
    this.buffers = new Map();

    // Active sound sources for cleanup
    this.activeSources = new Set();

    // Volume settings
    this.volumes = { ...AUDIO_CONFIG.DEFAULT_VOLUMES };

    // Gain nodes for each category
    this.gainNodes = {};

    // Current ambience track
    this.currentAmbience = null;

    // Mute state
    this.muted = false;
  }

  /**
   * Initialize the audio context (must be called after user interaction)
   */
  async initialize() {
    if (this.initialized) return;

    try {
      // Create audio context
      this.audioContext = new (window.AudioContext || window.webkitAudioContext)();

      // Create gain nodes for each category
      this.gainNodes = {
        ambience: this.audioContext.createGain(),
        music: this.audioContext.createGain(),
        sfx: this.audioContext.createGain()
      };

      // Connect all gain nodes to destination
      Object.values(this.gainNodes).forEach(node => {
        node.connect(this.audioContext.destination);
      });

      // Load saved volumes from localStorage
      this.loadVolumeSettings();

      // Apply initial volume settings
      this.applyVolumes();

      this.initialized = true;

      // Preload critical sounds
      this.preloadSounds(AUDIO_CONFIG.PRELOAD_SOUNDS);
    } catch (error) {
      console.error('Failed to initialize audio:', error);
    }
  }

  /**
   * Load volume settings from localStorage
   */
  loadVolumeSettings() {
    try {
      const saved = localStorage.getItem(AUDIO_CONFIG.STORAGE_KEY);
      if (saved) {
        this.volumes = { ...AUDIO_CONFIG.DEFAULT_VOLUMES, ...JSON.parse(saved) };
      }
    } catch (error) {
      console.warn('Failed to load volume settings:', error);
    }
  }

  /**
   * Save volume settings to localStorage
   */
  saveVolumeSettings() {
    try {
      localStorage.setItem(AUDIO_CONFIG.STORAGE_KEY, JSON.stringify(this.volumes));
    } catch (error) {
      console.warn('Failed to save volume settings:', error);
    }
  }

  /**
   * Apply current volume settings to gain nodes
   */
  applyVolumes() {
    if (!this.initialized) return;

    const masterVolume = this.muted ? 0 : this.volumes.master;

    this.gainNodes.ambience.gain.value = this.volumes.ambience * masterVolume;
    this.gainNodes.music.gain.value = this.volumes.music * masterVolume;
    this.gainNodes.sfx.gain.value = this.volumes.sfx * masterVolume;
  }

  /**
   * Set volume for a category
   */
  setVolume(category, value) {
    if (!this.initialized) return;

    // Clamp value between 0 and 1
    const clampedValue = Math.max(0, Math.min(1, value));

    if (category === 'master') {
      this.volumes.master = clampedValue;
    } else if (this.gainNodes[category]) {
      this.volumes[category] = clampedValue;
    }

    this.applyVolumes();
    this.saveVolumeSettings();
  }

  /**
   * Mute/unmute all audio
   */
  setMuted(muted) {
    this.muted = muted;
    this.applyVolumes();
  }

  /**
   * Get current volume for a category
   */
  getVolume(category) {
    return this.volumes[category] || 0;
  }

  /**
   * Preload sounds for faster playback
   */
  async preloadSounds(soundIds) {
    if (!this.initialized) return;

    const promises = soundIds.map(soundId => this.loadSound(soundId));
    await Promise.allSettled(promises);
  }

  /**
   * Load a sound file and return the audio buffer
   */
  async loadSound(soundId) {
    if (!this.initialized) return null;

    const soundDef = SOUND_REGISTRY[soundId];
    if (!soundDef) {
      console.warn(`Sound ID not found: ${soundId}`);
      return null;
    }

    // Get the base path (handle variations by getting first one for preloading)
    const basePath = soundDef.variations ? soundDef.variations[0] : soundDef.path;
    const path = basePath + AUDIO_CONFIG.AUDIO_FILE_EXTENSION;

    // Check if already loaded
    if (this.buffers.has(path)) {
      return this.buffers.get(path);
    }

    try {
      const response = await fetch(path);
      const arrayBuffer = await response.arrayBuffer();
      const audioBuffer = await this.audioContext.decodeAudioData(arrayBuffer);

      this.buffers.set(path, audioBuffer);
      return audioBuffer;
    } catch (error) {
      console.error(`Failed to load sound: ${path}`, error);
      return null;
    }
  }

  /**
   * Select a random variation from a sound with multiple files
   */
  selectVariation(soundId) {
    const soundDef = SOUND_REGISTRY[soundId];
    if (!soundDef) return null;

    if (soundDef.variations) {
      const randomIndex = Math.floor(Math.random() * soundDef.variations.length);
      return soundDef.variations[randomIndex];
    }

    return soundDef.path;
  }

  /**
   * Play a sound by ID
   */
  async playSound(soundId, options = {}) {
    if (!this.initialized) {
      await this.initialize();
    }

    const soundDef = SOUND_REGISTRY[soundId];
    if (!soundDef) {
      console.warn(`Sound ID not found: ${soundId}`);
      return null;
    }

    // Select base path (random variation if applicable)
    const basePath = this.selectVariation(soundId);
    const path = basePath + AUDIO_CONFIG.AUDIO_FILE_EXTENSION;

    // Load buffer if not cached
    let buffer = this.buffers.get(path);
    if (!buffer) {
      const response = await fetch(path);
      const arrayBuffer = await response.arrayBuffer();
      buffer = await this.audioContext.decodeAudioData(arrayBuffer);
      this.buffers.set(path, buffer);
    }

    // Create source
    const source = this.audioContext.createBufferSource();
    source.buffer = buffer;

    // Set loop if defined
    if (soundDef.loop) {
      source.loop = true;
    }

    // Connect to appropriate gain node
    const category = soundDef.category || 'sfx';
    source.connect(this.gainNodes[category]);

    // Track active source
    this.activeSources.add(source);

    // Clean up when sound ends
    source.onended = () => {
      this.activeSources.delete(source);
      source.disconnect();
    };

    // Start playback
    source.start(0);

    return source;
  }

  /**
   * Play multiple dice sounds with staggered timing
   */
  async playDice(diceType, count) {
    if (!this.initialized) {
      await this.initialize();
    }

    const soundId = diceType === 'd20' ? 'd20_roll' : 'd6_roll';
    const { DICE_STAGGER_MIN_MS, DICE_STAGGER_MAX_MS } = AUDIO_CONFIG;

    for (let i = 0; i < count; i++) {
      await this.playSound(soundId);

      // Add delay between dice (except for the last one)
      if (i < count - 1) {
        const delay = DICE_STAGGER_MIN_MS + Math.random() * (DICE_STAGGER_MAX_MS - DICE_STAGGER_MIN_MS);
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }
  }

  /**
   * Crossfade to a new ambience track
   */
  async setAmbience(trackId, duration = AUDIO_CONFIG.CROSSFADE_DURATION_S) {
    if (!this.initialized) {
      await this.initialize();
    }

    const soundDef = SOUND_REGISTRY[trackId];
    if (!soundDef || soundDef.category !== 'ambience') {
      console.warn(`Invalid ambience track: ${trackId}`);
      return;
    }

    // If same track is already playing, do nothing
    if (this.currentAmbience && this.currentAmbience.trackId === trackId) {
      return;
    }

    const currentTime = this.audioContext.currentTime;

    // Load and start new track at volume 0
    const basePath = soundDef.path;
    const path = basePath + AUDIO_CONFIG.AUDIO_FILE_EXTENSION;
    let buffer = this.buffers.get(path);
    if (!buffer) {
      const response = await fetch(path);
      const arrayBuffer = await response.arrayBuffer();
      buffer = await this.audioContext.decodeAudioData(arrayBuffer);
      this.buffers.set(path, buffer);
    }

    // Create new source
    const newSource = this.audioContext.createBufferSource();
    newSource.buffer = buffer;
    newSource.loop = true;

    // Create dedicated gain node for crossfading
    const newGainNode = this.audioContext.createGain();
    newGainNode.gain.value = 0; // Start at 0
    newSource.connect(newGainNode);
    newGainNode.connect(this.gainNodes.ambience);

    // Start new track
    newSource.start(0);

    // Fade out old track
    if (this.currentAmbience) {
      const oldGainNode = this.currentAmbience.gainNode;
      const oldSource = this.currentAmbience.source;

      oldGainNode.gain.linearRampToValueAtTime(0, currentTime + duration);

      // Stop old track after fade
      setTimeout(() => {
        oldSource.stop();
        oldSource.disconnect();
        oldGainNode.disconnect();
      }, duration * 1000 + 100);
    }

    // Fade in new track
    newGainNode.gain.linearRampToValueAtTime(1, currentTime + duration);

    // Save current ambience
    this.currentAmbience = {
      trackId,
      source: newSource,
      gainNode: newGainNode
    };
  }

  /**
   * Stop all currently playing sounds
   */
  stopAll() {
    // Stop all active sources
    this.activeSources.forEach(source => {
      try {
        source.stop();
        source.disconnect();
      } catch (e) {
        // Source may already be stopped
      }
    });
    this.activeSources.clear();

    // Stop current ambience
    if (this.currentAmbience) {
      try {
        this.currentAmbience.source.stop();
        this.currentAmbience.source.disconnect();
        this.currentAmbience.gainNode.disconnect();
      } catch (e) {
        // May already be stopped
      }
      this.currentAmbience = null;
    }
  }

  /**
   * Cleanup resources
   */
  async cleanup() {
    this.stopAll();

    if (this.audioContext) {
      await this.audioContext.close();
      this.audioContext = null;
    }

    this.buffers.clear();
    this.initialized = false;
  }
}

// Create singleton instance
export const audioManager = new AudioManager();
